"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FINANCE_REFUSALS = exports.financeListQuerySchema = exports.updateIncomeSchema = exports.updateExpenseSchema = exports.createIncomeSchema = exports.createExpenseSchema = exports.SUGGESTED_INCOME_CATEGORIES = exports.SUGGESTED_EXPENSE_CATEGORIES = exports.FINANCE_ENTRY_KINDS = void 0;
exports.defaultCashSource = defaultCashSource;
const zod_1 = require("zod");
const cash_drawer_1 = require("./cash-drawer");
/**
 * Manual expenses and incomes — SPEC §5.7.
 *
 * These are the two lines of the Overall Report that no other part of the
 * system can produce. Reservation, cafe and refill takings are derived from
 * bills; rent, wages, a supplier invoice or a one-off cash sale are not derived
 * from anything and have to be typed in. Until they can be, `Expenses` is
 * permanently zero and Net Profit is overstated by everything the venue spends.
 *
 * Money crosses the wire as decimal strings of millidinars, as everywhere else.
 */
exports.FINANCE_ENTRY_KINDS = ['EXPENSE', 'INCOME'];
/**
 * Suggested categories. Deliberately NOT an enum in the database — a venue will
 * have costs nobody anticipated, and a schema migration is a poor answer to
 * "we started paying for a water delivery". The UI offers these and accepts
 * free text.
 */
exports.SUGGESTED_EXPENSE_CATEGORIES = [
    'Rent',
    'Wages',
    'Electricity',
    'Internet',
    'Supplies',
    'Maintenance',
    'Marketing',
    'Other',
];
exports.SUGGESTED_INCOME_CATEGORIES = [
    'Equipment sale',
    'Event hire',
    'Sponsorship',
    'Other',
];
const amountIqd = zod_1.z
    .number()
    .int('Enter a whole number of dinars.')
    .positive('Enter an amount greater than zero.')
    .max(1_000_000_000, 'That amount looks wrong — check it before saving.');
const baseEntry = {
    description: zod_1.z.string().trim().min(1, 'Describe what this was for.').max(300),
    category: zod_1.z.string().trim().max(80).optional(),
    amountIqd,
    note: zod_1.z.string().trim().max(1000).optional(),
    /**
     * Which business day it belongs to, `YYYY-MM-DD`. Optional — defaults to the
     * venue's current business day, which is what an employee entering today's
     * costs means. Settable so last night's receipt found this morning still
     * lands on last night, rather than silently moving money between two days'
     * profit figures.
     */
    businessDate: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date format YYYY-MM-DD.')
        .optional(),
};
exports.createExpenseSchema = zod_1.z.object({
    ...baseEntry,
    /** Receipt photo, uploaded through the media endpoint first. */
    attachmentMediaAssetId: zod_1.z.string().min(1).optional(),
    /**
     * Cash drawer (owner, 2026-10-12): "Paid from: Drawer / Safe / Outside".
     * DRAWER is refused while the drawer is not in use (`drawer_not_enabled`);
     * SAFE writes an EXPENSE_PAID safe slip; OUTSIDE touches neither. No
     * `.default()`: a default here would be copied into every PATCH that
     * leaves the source alone.
     */
    paidFrom: zod_1.z.enum(cash_drawer_1.CASH_SOURCES).optional(),
});
exports.createIncomeSchema = zod_1.z.object({
    ...baseEntry,
    /** "Received into: Drawer / Safe / Outside" — `paidFrom`'s twin; SAFE writes INCOME_RECEIVED. */
    receivedInto: zod_1.z.enum(cash_drawer_1.CASH_SOURCES).optional(),
});
/*
 * The update schemas are written out field by field, never
 * `createX.partial()`: zod 4's `.partial()` keeps a field's `.default()`, so a
 * default added to a create schema later would silently rewrite that field on
 * every PATCH. Every field is optional; absent means "leave it as it is".
 */
exports.updateExpenseSchema = zod_1.z.object({
    description: baseEntry.description.optional(),
    category: baseEntry.category,
    amountIqd: amountIqd.optional(),
    note: baseEntry.note,
    businessDate: baseEntry.businessDate,
    attachmentMediaAssetId: zod_1.z.string().min(1).optional(),
    paidFrom: zod_1.z.enum(cash_drawer_1.CASH_SOURCES).optional(),
});
exports.updateIncomeSchema = zod_1.z.object({
    description: baseEntry.description.optional(),
    category: baseEntry.category,
    amountIqd: amountIqd.optional(),
    note: baseEntry.note,
    businessDate: baseEntry.businessDate,
    receivedInto: zod_1.z.enum(cash_drawer_1.CASH_SOURCES).optional(),
});
/**
 * The source a new entry gets when none is sent, and the one the staff form
 * starts from. While the drawer is in use, staff typing an expense are usually
 * paying from it and the owner usually is not. Before the drawer there is
 * nothing to say, so the row stays unknown (null), as every older row is.
 */
function defaultCashSource(drawerEnabled, isOwner) {
    if (!drawerEnabled)
        return null;
    return isOwner ? 'OUTSIDE' : 'DRAWER';
}
exports.financeListQuerySchema = zod_1.z.object({
    from: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    to: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    category: zod_1.z.string().trim().max(80).optional(),
    search: zod_1.z.string().trim().max(120).optional(),
});
/**
 * Refusal codes of the expense and income routes, for the staff app to
 * translate — beside `drawer_not_enabled` (DRAWER_REFUSALS) and the guard's
 * `missing_permission` (moving safe money without `safe.manage`).
 */
exports.FINANCE_REFUSALS = ['finance_entry_frozen'];
//# sourceMappingURL=finance.js.map